import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import {ControlEthnic, ControlGenderECDS} from '../../../../controls/agyle/general';
import {
    AccessibleInforProfReq,
    AccommodationStatus,
    InterpreterLanguage,
    SpokenLanguage,
} from '../../../../controls/agyle/ecds';
import _ from 'lodash';
import {OverseasVisitorChargingCategory} from '../../../../controls/agyle/dictionaries';
import {useStylesECDS} from './ecdsStyle';
import {useSelector} from 'react-redux';
import {
    accessibleInforProfReq,
    accommodationStatus,
    ethnicGroupsDict,
    gendersDict,
    interpreterLanguage,
    overseasVisitorChargingCategory,
    spokenLanguage,
} from '../../../../store/reducers/dicts';
import config from '../../../../app/config';
import {cleanObject} from '../../../../util/patientContextUtil';

const Characteristics = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();
    const {version, isSmall} = props;

    // Patient Characteristics Dataset
    const [characteristics, setCharacteristics] = useState({
        PersonStatedGenderCode: null,
        EthnicCategory: null,
        EthnicCategory2021: null,
        AccommodationStatus_SnomedCt: null,
        PreferredSpokenLanguage_SnomedCt: null,
        AccessibleInformationProfessionalRequiredCode_SnomedCt: null,
        InterpreterLanguage_SnomedCt: null,
        OverseasVisitorChargingCategoryAtCdsActivityDate: null,
    });

    // For ReadOnly Data
    const disableData = useRef({});

    // Validation
    const validationEnabled = config?.modules?.forms?.ecds?.validation || false;
    const [error, setError] = useState({
        gender: false,
        ethnic: false,
        language: false,
        interLanguage: false,
        accommodation: false,
        accessible: false,
        chargingCategory: false,
    });

    // Dictionaries
    const genderDict = useSelector(gendersDict);
    const ethnicDict = useSelector(ethnicGroupsDict);
    const accommodationDict = useSelector(accommodationStatus);
    const spokenLanguageDict = useSelector(spokenLanguage);
    const interpreterLanguageDict = useSelector(interpreterLanguage);
    const interStatusDict = useSelector(accessibleInforProfReq);
    const overseasVisitorChargingCategoryDict = useSelector(overseasVisitorChargingCategory);

    // Initial Data
    useEffect(() => {
        let data = {};
        let dataToBlock = {};

        if (props.patient?.studies?.EmergencyCare?.PersonGroupPatient?.PatientCharacteristics_EmergencyCare) {
            data = {
                ...props.patient?.studies?.EmergencyCare?.PersonGroupPatient?.PatientCharacteristics_EmergencyCare,
            };
        }

        if (props.patient.patient) {
            let interpreter = _.find(interStatusDict, {ecdsVersion: version, snomedCode: '315595002'});
            if (props?.patient?.patient?.interpreterRequired) interpreter = _.find(interStatusDict, {
                ecdsVersion: version,
                snomedCode: '315594003',
            });
            if (props?.patient?.patient?.interpreterRefused) interpreter = _.find(interStatusDict, {
                ecdsVersion: version,
                snomedCode: '759691000000101',
            });

            dataToBlock = {
                PersonStatedGenderCode: _.find(genderDict, {__code: props.patient.patient?.genderCode}),
                EthnicCategory: _.find(ethnicDict, {__code: props?.patient?.patient?.ethnicGroup?.__code}),
                AccommodationStatus_SnomedCt: _.find(accommodationDict, {__code: props.patient?.patient?.accommodationStatus?.__code}),
                PreferredSpokenLanguage_SnomedCt: _.find(spokenLanguageDict, dict => dict?.ecdsVersion === version && dict?.__description.toLowerCase() === props?.patient?.patient?.primaryLanguage?.toLowerCase()),
                InterpreterLanguage_SnomedCt: _.find(interpreterLanguageDict, dict => dict?.ecdsVersion === version && dict?.__description.toLowerCase() === props?.patient?.patient?.interpreterLanguage?.toLowerCase()),
                AccessibleInformationProfessionalRequiredCode_SnomedCt: interpreter,
                OverseasVisitorChargingCategoryAtCdsActivityDate: _.find(overseasVisitorChargingCategoryDict, {__code: props?.patient?.patient?.overseasVisitorChargingCategory?.__code}),
            };
        }

        data = {
            ...data,
            AccommodationStatus_SnomedCt: data?.AccommodationStatus_SnomedCt
                ? _.find(accommodationDict, {
                    ecdsVersion: version,
                    snomedCode: data?.AccommodationStatus_SnomedCt,
                }) : null,
            PreferredSpokenLanguage_SnomedCt: data?.PreferredSpokenLanguage_SnomedCt
                ? _.find(spokenLanguageDict, {
                    ecdsVersion: version,
                    snomedCode: data?.PreferredSpokenLanguage_SnomedCt,
                }) : null,
            AccessibleInformationProfessionalRequiredCode_SnomedCt: data?.AccessibleInformationProfessionalRequiredCode_SnomedCt
                ? _.find(interStatusDict, {
                    ecdsVersion: version,
                    snomedCode: data?.AccessibleInformationProfessionalRequiredCode_SnomedCt,
                }) : null,
            InterpreterLanguage_SnomedCt: data?.InterpreterLanguage_SnomedCt
                ? _.find(interpreterLanguageDict, {
                    ecdsVersion: version,
                    snomedCode: data?.InterpreterLanguage_SnomedCt,
                }) : null,
            OverseasVisitorChargingCategoryAtCdsActivityDate: data?.OverseasVisitorChargingCategoryAtCdsActivityDate
                ? _.find(overseasVisitorChargingCategoryDict, {__code: data?.OverseasVisitorChargingCategoryAtCdsActivityDate}) : null,
        };

        data = {
            ...data,
            ...dataToBlock,
        };

        cleanObject(data);

        setCharacteristics((prevState) => ({...prevState, ...data}));
        disableData.current = {...dataToBlock};
    }, [version]);

    const updateData = () => {
        const data = {
            PersonStatedGenderCode: characteristics.PersonStatedGenderCode ? characteristics.PersonStatedGenderCode.__code : '',
            EthnicCategory: characteristics.EthnicCategory ? characteristics.EthnicCategory.__code : '',
            EthnicCategory2021: characteristics.EthnicCategory2021,
            AccommodationStatus_SnomedCt: characteristics.AccommodationStatus_SnomedCt ? characteristics.AccommodationStatus_SnomedCt.snomedCode : '',
            PreferredSpokenLanguage_SnomedCt: characteristics.PreferredSpokenLanguage_SnomedCt ? characteristics.PreferredSpokenLanguage_SnomedCt.snomedCode : '',
            AccessibleInformationProfessionalRequiredCode_SnomedCt: characteristics.AccessibleInformationProfessionalRequiredCode_SnomedCt ? characteristics.AccessibleInformationProfessionalRequiredCode_SnomedCt.snomedCode : '',
            InterpreterLanguage_SnomedCt: characteristics.InterpreterLanguage_SnomedCt ? characteristics.InterpreterLanguage_SnomedCt.snomedCode : '',
            OverseasVisitorChargingCategoryAtCdsActivityDate: characteristics.OverseasVisitorChargingCategoryAtCdsActivityDate
                ? characteristics.OverseasVisitorChargingCategoryAtCdsActivityDate?.__code : '',
        };
        props.setData(data);
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (validate()) {
                updateData();
            }
        }, 400);

        return () => clearTimeout(timeoutId);
    }, [characteristics]);

    const setDataDictionary = (data) => {
        changeHandler({
            target: {
                name: data[0],
                value: data?.[1] || null,
            },
        });
    };

    const changeHandler = (event) => {
        setCharacteristics((prevState) => ({...prevState, [event.target.name]: event.target.value}));
    };

    const validate = () => {
        let check = true;

        if (validationEnabled) {
            const errorData = {
                gender: characteristics.PersonStatedGenderCode ? !props.checkLenght(1, 1, characteristics.PersonStatedGenderCode) : false,
                ethnic: characteristics.EthnicCategory ? !props.checkLenght(1, 2, characteristics.EthnicCategory.__code) : false,
                language: characteristics.PreferredSpokenLanguage_SnomedCt ? !props.checkLenght(6, 18, characteristics.PreferredSpokenLanguage_SnomedCt) : false,
                interLanguage: characteristics.InterpreterLanguage_SnomedCt ? !props.checkLenght(6, 18, characteristics.InterpreterLanguage_SnomedCt) : false,
                accommodation: characteristics.AccommodationStatus_SnomedCt ? !props.checkLenght(6, 18, characteristics.AccommodationStatus_SnomedCt) : false,
                accessible: characteristics.AccessibleInformationProfessionalRequiredCode_SnomedCt ? !props.checkLenght(6, 18, characteristics.AccessibleInformationProfessionalRequiredCode_SnomedCt) : false,
                chargingCategory: characteristics.OverseasVisitorChargingCategoryAtCdsActivityDate ? !props.checkLenght(1, 1, characteristics.OverseasVisitorChargingCategoryAtCdsActivityDate?.__code) : false,
            };

            setError(prevState => ({...prevState, ...errorData}));

            for (let prop in errorData) {
                if (errorData[prop]) {
                    check = false;
                    break;
                }
            }

            if (props.valid) {
                const validObj = {
                    name: 'Patient Characteristics',
                    groupStatus: 'R',
                    mandatory: false,
                    errors: !check,
                    pickers: false,
                };

                props.valid(prevState => ({...prevState, 'characteristics': validObj}));
            }
        }
        return check;
    };

    if (props.accordion !== 'Patient Characteristics') return null;

    return (
        <div className={classes.root}>
            <Grid container direction="row" spacing={2}>
                <Grid item xs={isSmall ? 12 : 6}>
                    <ControlGenderECDS
                        error={error.gender}
                        label={t('Gender')}
                        name="PersonStatedGenderCode"
                        value={characteristics.PersonStatedGenderCode}
                        disabled={!!disableData?.current.PersonStatedGenderCode}
                        onChange={(_e, val) => setDataDictionary(['PersonStatedGenderCode', val])}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <ControlEthnic
                        error={error.ethnic}
                        label={t('Ethnic category')}
                        name="EthnicCategory"
                        value={characteristics.EthnicCategory}
                        disabled
                        onChange={(_e, val) => setDataDictionary(['EthnicCategory', val])}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <SpokenLanguage
                        name="PreferredSpokenLanguage_SnomedCt"
                        label={t('Preferred Spoken Language')}
                        color="primary"
                        value={characteristics.PreferredSpokenLanguage_SnomedCt}
                        onChange={(_e, val) => setDataDictionary(['PreferredSpokenLanguage_SnomedCt', val])}
                        disabled={!!disableData?.current.PreferredSpokenLanguage_SnomedCt}
                        error={error.language}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AccessibleInforProfReq
                        name="AccessibleInformationProfessionalRequiredCode_SnomedCt"
                        label={t('Interpreter Status')}
                        color="primary"
                        value={characteristics.AccessibleInformationProfessionalRequiredCode_SnomedCt}
                        onChange={(_e, val) => setDataDictionary(['AccessibleInformationProfessionalRequiredCode_SnomedCt', val])}
                        disabled={!!disableData?.current.AccessibleInformationProfessionalRequiredCode_SnomedCt}
                        error={error.accessible}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <InterpreterLanguage
                        name="InterpreterLanguage_SnomedCt"
                        label={t('Interpreter Language')}
                        color="primary"
                        value={characteristics.InterpreterLanguage_SnomedCt}
                        onChange={(_e, val) => setDataDictionary(['InterpreterLanguage_SnomedCt', val])}
                        disabled={!!disableData?.current.InterpreterLanguage_SnomedCt || characteristics.AccessibleInformationProfessionalRequiredCode_SnomedCt?.snomedCode === '759691000000101'}
                        error={error.interLanguage}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AccommodationStatus
                        name="AccommodationStatus_SnomedCt"
                        label={t('Accommodation status')}
                        color="primary"
                        value={characteristics.AccommodationStatus_SnomedCt}
                        onChange={(_e, val) => setDataDictionary(['AccommodationStatus_SnomedCt', val])}
                        disabled={!!disableData?.current.AccommodationStatus_SnomedCt}
                        error={error.accommodation}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <OverseasVisitorChargingCategory
                        name="OverseasVisitorChargingCategoryAtCdsActivityDate"
                        label={t('Overseas Visitor Charging Category')}
                        color="primary"
                        value={characteristics.OverseasVisitorChargingCategoryAtCdsActivityDate}
                        onChange={(_e, val) => setDataDictionary(['OverseasVisitorChargingCategoryAtCdsActivityDate', val])}
                        error={error.chargingCategory}
                        disabled={!!disableData?.current.OverseasVisitorChargingCategoryAtCdsActivityDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default Characteristics;
